import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";

import { listProspects } from "../services/onboarding/prospects";
import {
  OnboardingState,
  ProspectSummary,
  ProspectType,
  SetupFeeStatus,
  StageCount,
  StationReadiness,
} from "../types/onboarding";
import {
  MORE_INFO_NEEDED,
  PIPELINE_STATES,
  RESIGNED,
  stateLabel,
} from "../utils/onboardingStates";
import { prospectTypeOf } from "../utils/prospectType";

interface UseProspectsParams {
  toastRef: React.RefObject<Toast>;
}

interface UseProspects {
  prospects: ProspectSummary[];
  loading: boolean;
  error: boolean;
  stateFilter: OnboardingState | null;
  setStateFilter: (state: OnboardingState | null) => void;
  // The readiness filter composes with the stage filter: the queue of prospects
  // waiting on an agent, optionally narrowed to one stage (CWA-154).
  readinessFilter: StationReadiness | null;
  setReadinessFilter: (readiness: StationReadiness | null) => void;
  // CWA-196: composes with both, e.g. promoted customers with an open fee.
  setupFeeFilter: SetupFeeStatus | null;
  setSetupFeeFilter: (status: SetupFeeStatus | null) => void;
  // CWA-184: new customers or additional stations; composes with the others.
  typeFilter: ProspectType | null;
  setTypeFilter: (type: ProspectType | null) => void;
  // CWA-189: only the prospects whose reminders escalated; composes with the others.
  followUpFilter: boolean;
  setFollowUpFilter: (onlyFollowUp: boolean) => void;
  counts: StageCount[];
  reload: () => void;
}

// Pipeline order first, then the two special statuses.
const COUNT_ORDER: OnboardingState[] = [
  ...PIPELINE_STATES,
  MORE_INFO_NEEDED,
  RESIGNED,
];

/**
 * Owns the prospects list: loads from the admin endpoint, holds the state
 * filter, and surfaces load failures via Toast so an empty grid isn't mistaken
 * for "no prospects". Mirrors the data-in-hooks convention.
 *
 * The list is fetched **once, unfiltered**, and the filter is applied here. The
 * endpoint is unpaginated (it returns every prospect either way), so this is
 * identical in output while making the per-stage counts possible at all — a
 * server-filtered array can only ever count the stage already selected.
 */
const useProspects = ({ toastRef }: UseProspectsParams): UseProspects => {
  const [prospects, setProspects] = useState<ProspectSummary[]>([]);
  const [readinessFilter, setReadinessFilter] =
    useState<StationReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stateFilter, setStateFilter] = useState<OnboardingState | null>(null);
  const [setupFeeFilter, setSetupFeeFilter] = useState<SetupFeeStatus | null>(
    null
  );
  const [typeFilter, setTypeFilter] = useState<ProspectType | null>(null);
  const [followUpFilter, setFollowUpFilter] = useState(false);

  // Guard against out-of-order responses when reload is triggered repeatedly.
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(false);
    try {
      const data = await listProspects();
      if (seq !== loadSeqRef.current) return;
      setProspects(data?.prospects ?? []);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      console.error("Failed to load prospects", e);
      setError(true);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load prospects.",
        life: 3000,
      });
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [toastRef]);

  useEffect(() => {
    load();
  }, [load]);

  // Counts come from the unfiltered list, so they stay stable while a filter is
  // applied — which is the whole point of showing them.
  const counts = useMemo<StageCount[]>(() => {
    const tally = new Map<OnboardingState, number>();
    prospects.forEach((p) => {
      tally.set(p.current_state, (tally.get(p.current_state) ?? 0) + 1);
    });
    return COUNT_ORDER.filter((state) => tally.has(state)).map((state) => ({
      state,
      label: stateLabel(state),
      count: tally.get(state) as number,
    }));
  }, [prospects]);

  // Both filters are applied here rather than by the endpoint. The list is
  // fetched unfiltered because the per-stage counts need the whole set, and the
  // backend's readiness query parameter would zero them the same way the stage
  // one would (research R1).
  const visible = useMemo(
    () =>
      prospects.filter(
        (p) =>
          (!stateFilter || p.current_state === stateFilter) &&
          (!readinessFilter || p.station_readiness === readinessFilter) &&
          (!setupFeeFilter || p.setup_fee_status === setupFeeFilter) &&
          (!typeFilter || prospectTypeOf(p) === typeFilter) &&
          (!followUpFilter || p.needs_follow_up === true)
      ),
    [prospects, readinessFilter, setupFeeFilter, stateFilter, typeFilter, followUpFilter]
  );

  return {
    prospects: visible,
    loading,
    error,
    stateFilter,
    readinessFilter,
    setReadinessFilter,
    setStateFilter,
    setupFeeFilter,
    setSetupFeeFilter,
    typeFilter,
    setTypeFilter,
    followUpFilter,
    setFollowUpFilter,
    counts,
    reload: load,
  };
};

export default useProspects;
